import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/cn';

const stackVariants = cva('flex', {
	variants: {
		direction: {
			column: 'flex-col',
			row: 'flex-row',
			rowReverse: 'flex-row-reverse',
			columnReverse: 'flex-col-reverse',
		},
		gap: {
			none: 'gap-0',
			xxs: 'gap-1',
			xs: 'gap-1.5',
			sm: 'gap-2',
			md: 'gap-3',
			lg: 'gap-4',
			xl: 'gap-5',
			xxl: 'gap-6',
		},
		align: {
			start: 'items-start',
			center: 'items-center',
			end: 'items-end',
			stretch: 'items-stretch',
			baseline: 'items-baseline',
		},
		justify: {
			start: 'justify-start',
			center: 'justify-center',
			end: 'justify-end',
			between: 'justify-between',
			around: 'justify-around',
			evenly: 'justify-evenly',
		},
		wrap: {
			true: 'flex-wrap',
			false: 'flex-nowrap',
		},
		flex: {
			none: 'flex-none',
			auto: 'flex-auto',
			one: 'flex-1 min-w-0',
		},
		fullWidth: {
			true: 'w-full',
		},
		/** Bottom margin toward the next sibling (page rhythm). */
		mb: {
			sm: 'mb-2',
			md: 'mb-4',
			lg: 'mb-4',
			xl: 'mb-6',
		},
	},
	defaultVariants: {
		direction: 'column',
		gap: 'none',
	},
});

type StackElement =
	| 'div'
	| 'section'
	| 'article'
	| 'aside'
	| 'ol'
	| 'ul'
	| 'li'
	| 'nav'
	| 'main'
	| 'header'
	| 'footer';

export interface StackProps extends React.HTMLAttributes<HTMLElement> {
	/** Elemento HTML da renderizzare (div, section, ul, ecc.). Default: `div`. */
	as?: StackElement;
	/** Direzione del flex layout. Default: `column`. */
	direction?: VariantProps<typeof stackVariants>['direction'];
	/** Spaziatura tra i figli. Default: `none`. */
	gap?: VariantProps<typeof stackVariants>['gap'];
	/** Allineamento sull'asse trasversale. */
	align?: VariantProps<typeof stackVariants>['align'];
	/** Allineamento/distribuzione sull'asse principale. */
	justify?: VariantProps<typeof stackVariants>['justify'];
	/** Se true, i figli vanno a capo invece di restare su una riga/colonna sola. */
	wrap?: VariantProps<typeof stackVariants>['wrap'];
	/** Comportamento flex-grow/shrink dello Stack stesso all'interno del suo genitore flex. */
	flex?: VariantProps<typeof stackVariants>['flex'];
	/** Se true, occupa tutta la larghezza disponibile. */
	fullWidth?: VariantProps<typeof stackVariants>['fullWidth'];
	/** Margine inferiore verso l'elemento successivo (ritmo verticale di pagina). */
	mb?: VariantProps<typeof stackVariants>['mb'];
}

/** Contenitore flex generico per allineare/spaziare i figli. `direction="column"` di default; usa {@link Row} per la variante orizzontale. */
const Stack = React.forwardRef<HTMLElement, StackProps>(
	(
		{ className, as = 'div', direction, gap, align, justify, wrap, flex, fullWidth, mb, ...props },
		ref,
	) => {
		const Comp = as;
		return (
			<Comp
				ref={ref as React.Ref<never>}
				className={cn(
					stackVariants({ direction, gap, align, justify, wrap, flex, fullWidth, mb, className }),
				)}
				{...props}
			/>
		);
	},
);
Stack.displayName = 'Stack';

/** Props di {@link Row}: come {@link StackProps} ma senza `direction`, che è fissa a `row`. */
export type RowProps = Omit<StackProps, 'direction'>;

/** {@link Stack} orizzontale: `align="center"` di default, dato che le righe inline quasi sempre vanno centrate verticalmente. */
const Row = React.forwardRef<HTMLDivElement, RowProps>(({ align = 'center', ...props }, ref) => (
	<Stack ref={ref} direction="row" align={align} {...props} />
));
Row.displayName = 'Row';

export { Row, Stack, stackVariants };
